<!--
  UploadVideoCom 视频上传组件
  - 支持两种上传模式:vod (Aliyun VOD Web 上传 SDK + STS) / oss (multipart /file/uploadFile)
  - PC 浏览器通过 JS 动态创建 <input type="file"> 触发选片
  - 对外暴露 chooseVideo(),触发后回调 uploadstarted / success / fail
-->
<template>
  <div class="upload-video-com">
    <!-- 触发按钮(可由调用方通过 slot 替换,作用域插槽暴露 chooseVideo) -->
    <slot name="trigger" :chooseVideo="chooseVideo" :disabled="disabled" :loading="statusPhase === 'uploading' || statusPhase === 'started'">
      <el-button
        :type="btnType"
        :icon="icon || undefined"
        :loading="statusPhase === 'uploading' || statusPhase === 'started'"
        :disabled="disabled"
        @click="chooseVideo"
      >
        {{ triggerText }}
      </el-button>
    </slot>

    <!-- 进度条 -->
    <div v-if="showProgress && statusPhase === 'uploading'" class="upload-progress">
      <el-progress :percentage="stsProgress" :stroke-width="10" />
      <div class="status-text">{{ statusText }}</div>
      <div class="upload-actions">
        <el-button size="mini" :disabled="pauseDisabled" @click="pauseUpload">
          暂停
        </el-button>
        <el-button size="mini" :disabled="resumeDisabled" @click="resumeUpload">
          恢复
        </el-button>
      </div>
    </div>

    <!-- 隐藏的 file input(VOD 模式不直接用,OSS 模式也不直接用,统一通过 chooseVideo 动态创建) -->
  </div>
</template>

<script>
import axios from "axios";
import serverConfig from "@/utils/config/index.js";
import { getVideoPathToken } from "@/utils/api/activity";

/* global AliyunUpload */

export default {
  name: "UploadVideoCom",
  props: {
    // 上传模式:vod(走阿里云点播)/ oss(走 /file/uploadFile)
    uploadMode: {
      type: String,
      default: "vod",
      validator: (val) => ["vod", "oss"].includes(val),
    },
    // 触发按钮文案
    triggerText: {
      type: String,
      default: "上传视频",
    },
    // 触发按钮图标类名
    icon: {
      type: String,
      default: "",
    },
    // 触发按钮类型
    btnType: {
      type: String,
      default: "primary",
    },
    // 最大文件大小(MB)
    maxSize: {
      type: Number,
      default: 10,
    },
    // input accept
    accept: {
      type: String,
      default: ".mp4",
    },
    // oss 模式传给后端的 type
    fileType: {
      type: String,
      default: "modularMP4",
    },
    // VOD 区域
    region: {
      type: String,
      default: "cn-shanghai",
    },
    // VOD userId
    userId: {
      type: [String, Number],
      default: "",
    },
    // VOD 转码模板组 ID(空则用 userData='{"Vod":{}}')
    templateGroupId: {
      type: String,
      default: "",
    },
    // 透传标记:'cover' / 'module' / 其他
    target: {
      type: String,
      default: "cover",
    },
    // 触发按钮是否 disabled
    disabled: {
      type: Boolean,
      default: false,
    },
    // 是否显示进度条与操作按钮
    showProgress: {
      type: Boolean,
      default: false,
    },
    // 分片大小(字节)
    partSize: {
      type: Number,
      default: 1048576,
    },
    // 并发数
    parallel: {
      type: Number,
      default: 5,
    },
    // 超时(毫秒)
    timeout: {
      type: Number,
      default: 60000,
    },
    // 重试
    retryCount: {
      type: Number,
      default: 3,
    },
    retryDuration: {
      type: Number,
      default: 2,
    },
  },
  data() {
    return {
      uploader: null,
      file: null,
      // 本地预览 URL(blob:),由 handleFileSelected 创建,在 success 中通过 payload.previewUrl 抛给父组件
      // 父组件可在 VOD 转码未完成时用它给 <video> 一个本地预览,轮询拿到 VOD URL 后再替换
      localPreviewUrl: "",
      stsProgress: 0,
      statusText: "",
      // idle / started / uploading / success / failed / canceled / expired
      statusPhase: "idle",
    };
  },
  computed: {
    uploadDisabled() {
      return this.statusPhase === "uploading" || this.statusPhase === "started";
    },
    pauseDisabled() {
      return this.statusPhase !== "uploading";
    },
    resumeDisabled() {
      return this.statusPhase !== "canceled";
    },
  },
  beforeDestroy() {
    // 组件销毁时停止上传,避免 setSTSToken 回调报错
    if (this.uploader && typeof this.uploader.stopUpload === "function") {
      try {
        this.uploader.stopUpload();
      } catch (e) {
        // ignore
      }
    }
    // 释放本地预览 blob URL,避免内存泄漏
    this._revokeLocalPreview();
  },
  methods: {
    /**
     * 释放本地预览 blob URL,避免内存泄漏
     */
    _revokeLocalPreview() {
      if (this.localPreviewUrl && /^blob:/.test(this.localPreviewUrl)) {
        try {
          URL.revokeObjectURL(this.localPreviewUrl);
        } catch (e) {
          // ignore
        }
        this.localPreviewUrl = "";
      }
    },
    /**
     * 触发选片。PC 端通过动态创建 input 唤起文件选择器
     */
    chooseVideo() {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = this.accept;
      input.style.display = "none";
      input.addEventListener("change", (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        this.handleFileSelected(file);
        // 清理
        document.body.removeChild(input);
      });
      document.body.appendChild(input);
      input.click();
    },

    /**
     * 校验文件并启动上传
     */
    async handleFileSelected(file) {
      // 1. 大小校验
      if (file.size / 1048576 > this.maxSize) {
        this.$message.warning(`请上传${this.maxSize}M 以内视频`);
        this.$emit("fail", {
          target: this.target,
          code: "SIZE_EXCEEDED",
          message: `文件超过${this.maxSize}MB`,
        });
        return;
      }
      // 2. 后缀校验(.mp4)
      const lastArr = file.name.split(".");
      if (lastArr[1] && lastArr[1].toLowerCase() !== "mp4") {
        this.$message.warning("请选择MP4格式视频");
        this.$emit("fail", {
          target: this.target,
          code: "INVALID_FORMAT",
          message: "文件格式不正确",
        });
        return;
      }

      this.file = file;
      this.statusPhase = "started";
      this.statusText = "准备上传...";
      this.stsProgress = 0;
      // 创建一个本地 blob URL 作为即时预览,在 success 事件中通过 payload.previewUrl 抛给父组件
      // 父组件可立即用它渲染 <video>,VOD 轮询拿到在线 URL 后再无缝替换
      this._revokeLocalPreview();
      try {
        this.localPreviewUrl = URL.createObjectURL(file);
      } catch (e) {
        this.localPreviewUrl = "";
      }
      this.$emit("uploadstarted", { target: this.target });

      // 规范化 mode(避免 kebab/camel 转换或大小写问题)
      const mode = String(this.uploadMode || "").toLowerCase();
      console.log(
        "[UploadVideoCom] start upload, target=%s, mode=%s, file=%s",
        this.target,
        mode,
        file.name,
      );
      if (mode === "vod") {
        this.startVodUpload(file);
      } else if (mode === "oss") {
        this.startOssUpload(file);
      } else {
        this.$message.error(`未知的上传模式: ${this.uploadMode}`);
        this.failUpload({
          uploadInfo: null,
          code: "INVALID_MODE",
          message: `unknown uploadMode: ${this.uploadMode}`,
        });
      }
    },

    /**
     * VOD 模式:阿里云点播 Web 上传 SDK
     */
    startVodUpload(file) {
      const self = this;
      // 已有 uploader 先停止
      if (self.uploader) {
        try {
          self.uploader.stopUpload();
        } catch (e) {
          // ignore
        }
      }
      // 防御:window.AliyunUpload 必须由 public/index.html 加载
      const VodCtor =
        (typeof window !== "undefined" &&
          window.AliyunUpload &&
          window.AliyunUpload.Vod) ||
        (typeof AliyunUpload !== "undefined" && AliyunUpload.Vod);
      if (typeof VodCtor !== "function") {
        console.error(
          "[UploadVideoCom] AliyunUpload.Vod 未找到,请确认 public/index.html 已引入 aliyun-upload-sdk",
        );
        this.failUpload({
          uploadInfo: null,
          code: "SDK_MISSING",
          message: "AliyunUpload SDK 未加载",
        });
        return;
      }

      const userData = self.templateGroupId
        ? JSON.stringify({ Vod: { TemplateGroupId: self.templateGroupId } })
        : '{"Vod":{}}';

      self.uploader = new VodCtor({
        timeout: self.timeout,
        partSize: Math.round(self.partSize),
        parallel: self.parallel,
        retryCount: self.retryCount,
        retryDuration: self.retryDuration,
        region: self.region,
        userId: self.userId,
        localCheckpoint: true,
        addFileSuccess: (uploadInfo) => {
          self.statusText = "添加文件成功,等待上传...";
          self.startUpload();
        },
        onUploadstarted: (uploadInfo) => {
          // 通过 serviceAxios 拿 STS Token,统一 token 注入
          // 注意:serviceAxios 的响应拦截器已 unwrap 一次,response 已经是 {status, data: {STS}}
          getVideoPathToken()
            .then((response) => {
              if (response && (response.status === 1 || response.code === 200)) {
                const data = response.data || {};
                self.uploader.setSTSToken(
                  uploadInfo,
                  data.AccessKeyId,
                  data.AccessKeySecret,
                  data.SecurityToken,
                );
              } else {
                self.failUpload({
                  uploadInfo,
                  code: "STS_FAILED",
                  message: "获取 STS 凭证失败",
                });
              }
            })
            .catch((err) => {
              self.failUpload({
                uploadInfo,
                code: "STS_ERROR",
                message: (err && err.message) || "获取 STS 凭证异常",
              });
            });
          self.statusPhase = "uploading";
          self.statusText = "文件开始上传...";
        },
        onUploadSucceed: (uploadInfo) => {
          self.statusPhase = "success";
          self.stsProgress = 100;
          self.statusText = "文件上传成功!";
          const payload = {
            target: self.target,
            videoId: uploadInfo.videoId,
            url: uploadInfo.endpoint
              ? uploadInfo.endpoint + "/" + uploadInfo.object
              : "",
            coverUrl: uploadInfo.coverUrl || "",
            // 本地预览 blob URL:VOD 转码/截图尚未完成时,父组件可以先用它渲染 <video>,
            // 轮询拿到在线 playUrl 后再无缝替换。父组件负责 revoke 旧 blob。
            previewUrl: self.localPreviewUrl || "",
            fileSize: file.size,
            fileName: file.name,
            raw: uploadInfo,
          };
          self.$emit("success", payload);
          // success 发出后,父组件会接管 blob URL 的生命周期(轮询后 revoke 旧值)
          self.localPreviewUrl = "";
        },
        onUploadFailed: (uploadInfo, code, message) => {
          self.failUpload({ uploadInfo, code, message });
        },
        onUploadCanceled: (uploadInfo, code, message) => {
          self.statusPhase = "canceled";
          self.statusText = "文件已暂停上传";
          self.$emit("fail", {
            target: self.target,
            code: code || "CANCELED",
            message: message || "已暂停",
          });
        },
        onUploadProgress: (uploadInfo, totalSize, progress) => {
          const percent = Math.ceil(progress * 100);
          self.stsProgress = percent;
          self.statusText = `文件上传中... ${percent}%`;
        },
        onUploadTokenExpired: () => {
          self.statusPhase = "expired";
          self.statusText = "文件上传超时";
          self.$message.error("文件上传超时");
          self.$emit("fail", {
            target: self.target,
            code: "TOKEN_EXPIRED",
            message: "STS 凭证过期",
          });
        },
        onUploadEnd: () => {
          // onUploadEnd 不修改 statusPhase,因为 onUploadSucceed/Failed 已经修改
          // 此处不做额外动作
        },
      });

      self.uploader.addFile(file, null, null, null, userData);
    },

    /**
     * OSS 模式:multipart /file/uploadFile
     */
    async startOssUpload(file) {
      const self = this;
      const loading = self.$loading
        ? self.$loading({
            lock: true,
            text: "上传中...",
            spinner: "el-icon-loading",
            background: "rgba(0, 0, 0, 0.7)",
          })
        : null;
      self.statusPhase = "uploading";
      self.statusText = "上传中...";
      self.stsProgress = 30; // 模拟"开始上传"

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", self.fileType);

        const response = await axios.post(
          serverConfig.baseURL + "/file/uploadFile",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "Access-Token": localStorage.getItem("token") || "",
            },
            onUploadProgress: (e) => {
              if (e.total) {
                self.stsProgress = Math.min(
                  99,
                  Math.ceil((e.loaded / e.total) * 100),
                );
                self.statusText = `上传中... ${self.stsProgress}%`;
              }
            },
          },
        );

        if (loading) loading.close();

        const resData = response && response.data ? response.data : {};
        if (resData.status === 1) {
          self.statusPhase = "success";
          self.stsProgress = 100;
          self.statusText = "上传成功";
          self.$emit("success", {
            target: self.target,
            videoId: "",
            url: resData.msg || "",
            coverUrl: "",
            fileSize: file.size,
            fileName: file.name,
            raw: resData,
          });
        } else {
          self.failUpload({
            uploadInfo: null,
            code: "OSS_FAILED",
            message: (resData && resData.msg) || "上传失败",
          });
        }
      } catch (err) {
        if (loading) loading.close();
        self.failUpload({
          uploadInfo: null,
          code: "OSS_ERROR",
          message: (err && err.message) || "上传异常",
        });
      }
    },

    /**
     * 通用失败处理
     */
    failUpload({ uploadInfo, code, message }) {
      this.statusPhase = "failed";
      this.statusText = "文件上传失败";
      this.$message.error("文件上传失败");
      this.$emit("fail", {
        target: this.target,
        code,
        message,
        uploadInfo,
      });
    },

    /**
     * VOD 模式:开始 / 恢复
     */
    startUpload() {
      if (this.uploader && typeof this.uploader.startUpload === "function") {
        this.statusPhase = "uploading";
        this.uploader.startUpload();
      }
    },
    pauseUpload() {
      if (this.uploader && typeof this.uploader.stopUpload === "function") {
        this.uploader.stopUpload();
        this.statusPhase = "canceled";
        this.statusText = "已暂停";
      }
    },
    resumeUpload() {
      if (this.uploader && typeof this.uploader.startUpload === "function") {
        this.statusPhase = "uploading";
        this.uploader.startUpload();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.upload-video-com {
  display: inline-block;
}
.upload-progress {
  margin-top: 10px;
  width: 280px;
  .status-text {
    margin-top: 4px;
    font-size: 12px;
    color: #606266;
  }
  .upload-actions {
    margin-top: 6px;
    text-align: right;
  }
}
</style>